/**
 * AREA CARD
 * 
 * Collapsed view of an area in the area list
 * Shows icon, label, dimensions summary, fixture counts
 */

import React from 'react';
import { ChevronRight, Trash2, Edit2 } from 'lucide-react';
import { getAreaType } from '../../constants/areaTypes';

const AreaCard = ({ area, onEdit, onRemove, disabled = false }) => {
  const areaConfig = getAreaType(area.type);
  
  // Count fixtures by condition
  const getFixtureCounts = () => {
    const fixtures = area.fixtures || {};
    let replace = 0;
    let addNew = 0;
    let keep = 0;
    
    Object.values(fixtures).forEach(f => {
      if (f.condition === 'exists_replace') replace++;
      if (f.condition === 'add_new') addNew++;
      if (f.condition === 'exists_keep') keep++;
    });
    
    return { replace, addNew, keep, total: replace + addNew + keep };
  };

  const counts = getFixtureCounts();
  const dims = area.dimensions || {};

  return (
    <div className="border border-slate-700 bg-slate-900/50 rounded overflow-hidden hover:border-slate-600 transition-all">
      <div className="flex items-center p-3">
        {/* Icon */}
        <div className="text-3xl mr-3">{areaConfig.icon}</div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-slate-200 truncate">{area.label}</h4>
            <span className="text-xs text-slate-500">({areaConfig.label})</span>
          </div>
          
          {/* Dimensions summary */}
          <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
            {dims.floor_sf && (
              <span>{dims.floor_sf} SF</span>
            )}
            {dims.length_ft && dims.width_ft && (
              <span>
                {dims.length_ft}'{dims.length_in || 0}" × {dims.width_ft}'{dims.width_in || 0}"
              </span>
            )}
            {counts.total > 0 && (
              <span className="flex items-center gap-1">
                {counts.replace > 0 && (
                  <span className="text-amber-400">{counts.replace} replace</span>
                )}
                {counts.addNew > 0 && (
                  <span className="text-emerald-400">{counts.addNew} new</span>
                )}
                {counts.keep > 0 && (
                  <span className="text-blue-400">{counts.keep} keep</span>
                )}
              </span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 ml-2">
          {!disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(area.area_id);
              }}
              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
              title="Remove area"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onEdit?.(area.area_id)}
            className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded"
            title="Edit area"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <ChevronRight className="w-5 h-5 text-slate-500" />
        </div>
      </div>
    </div>
  );
};

export default AreaCard;
